import { useState, useEffect } from "react";
import axios from "axios";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { ChartBar, TrendUp, Package, Truck, Timer, Lightning } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  received: "#71717A", info_needed: "#F59E0B", compounding: "#0055FF",
  shipped: "#8B5CF6", delivered: "#10B981"
};

const DELIVERY_LABELS = {
  express_post: "Express Post", own_courier: "Own Courier",
  urgent_express: "Urgent+Express", urgent_courier: "Urgent+Courier", pickup: "Pickup"
};

export default function AdminAnalyticsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const { data: d } = await axios.get(`${API}/admin/analytics`, { withCredentials: true });
        setData(d);
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    fetch();
  }, []);

  if (loading) return <div className="p-12 text-center font-mono text-sm text-zinc-500">LOADING ANALYTICS...</div>;
  if (!data) return <div className="p-12 text-center text-sm text-zinc-500">Failed to load analytics</div>;

  const statusData = Object.entries(data.orders_by_status || {}).map(([k, v]) => ({
    name: k.replace("_", " ").replace(/\b\w/g, c => c.toUpperCase()), value: v, fill: STATUS_COLORS[k] || "#71717A"
  }));

  const deliveryData = Object.entries(data.orders_by_delivery || {}).map(([k, v]) => ({
    name: DELIVERY_LABELS[k] || k, value: v
  }));

  const totalOrders = data.orders_by_month?.reduce((s, m) => s + m.count, 0) || 0;
  const totalRevenue = data.orders_by_month?.reduce((s, m) => s + m.revenue, 0) || 0;
  const ta = data.turnaround || { total_completed: 0, avg_hours: 0, avg_days: 0, fastest_hours: 0, slowest_hours: 0, stages: {}, by_month: [] };
  const stageList = Object.entries(ta.stages || {}).map(([k, v]) => ({ key: k, ...v }));
  const formatDuration = (h) => {
    if (!h && h !== 0) return "—";
    if (h < 1) return `${Math.round(h * 60)}m`;
    if (h < 48) return `${h.toFixed(1)}h`;
    return `${(h / 24).toFixed(1)}d`;
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="analytics-page">
      <div className="mb-6">
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">ADMIN</p>
        <h1 className="font-[Chivo] text-3xl sm:text-4xl font-black tracking-tighter text-zinc-950 mt-1">Analytics</h1>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-0 border border-zinc-200 mb-8" data-testid="analytics-summary">
        <div className="p-6 border-r border-b border-zinc-200">
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">Total Orders</p>
          <p className="font-[Chivo] text-3xl font-black text-zinc-950 mt-2">{totalOrders}</p>
        </div>
        <div className="p-6 border-r border-b border-zinc-200">
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">Total Revenue</p>
          <p className="font-[Chivo] text-3xl font-black text-zinc-950 mt-2">${totalRevenue.toFixed(0)}</p>
        </div>
        <div className="p-6 border-r border-b border-zinc-200">
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">Top Product</p>
          <p className="font-[Chivo] text-lg font-bold text-zinc-950 mt-2 truncate">{data.top_products?.[0]?.ingredient || "N/A"}</p>
        </div>
        <div className="p-6 border-b border-zinc-200">
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">Avg Order</p>
          <p className="font-[Chivo] text-3xl font-black text-zinc-950 mt-2">${totalOrders > 0 ? (totalRevenue / totalOrders).toFixed(0) : 0}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Orders Per Month */}
        <div className="border border-zinc-200 p-6" data-testid="orders-per-month-chart">
          <h3 className="font-[Chivo] font-bold text-zinc-950 mb-4 flex items-center gap-2">
            <ChartBar size={18} className="text-[#0055FF]" /> Orders Per Month
          </h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={data.orders_by_month}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
              <XAxis dataKey="label" tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} />
              <YAxis tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} />
              <Tooltip />
              <Bar dataKey="count" fill="#0055FF" name="Orders" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Revenue Per Month */}
        <div className="border border-zinc-200 p-6" data-testid="revenue-per-month-chart">
          <h3 className="font-[Chivo] font-bold text-zinc-950 mb-4 flex items-center gap-2">
            <TrendUp size={18} className="text-emerald-600" /> Revenue Per Month
          </h3>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={data.orders_by_month}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
              <XAxis dataKey="label" tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} />
              <YAxis tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} tickFormatter={v => `$${v}`} />
              <Tooltip formatter={v => `$${v.toFixed(2)}`} />
              <Line type="monotone" dataKey="revenue" stroke="#10B981" strokeWidth={2} dot={{ fill: "#10B981" }} name="Revenue" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Orders by Status */}
        <div className="border border-zinc-200 p-6" data-testid="orders-by-status-chart">
          <h3 className="font-[Chivo] font-bold text-zinc-950 mb-4 flex items-center gap-2">
            <Package size={18} className="text-violet-600" /> Orders by Status
          </h3>
          {statusData.length > 0 ? (
            <div className="flex items-center gap-6">
              <ResponsiveContainer width="50%" height={200}>
                <PieChart>
                  <Pie data={statusData} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={({ name, value }) => `${value}`}>
                    {statusData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-2">
                {statusData.map((s, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    <div className="w-3 h-3" style={{ backgroundColor: s.fill }}></div>
                    <span className="text-zinc-600">{s.name}</span>
                    <span className="font-mono font-semibold text-zinc-950">{s.value}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : <p className="text-sm text-zinc-500">No data yet</p>}
        </div>

        {/* Top Products */}
        <div className="border border-zinc-200 p-6" data-testid="top-products-chart">
          <h3 className="font-[Chivo] font-bold text-zinc-950 mb-4 flex items-center gap-2">
            <Truck size={18} className="text-amber-600" /> Top Ordered Products
          </h3>
          {data.top_products?.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={data.top_products} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                <XAxis type="number" tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} />
                <YAxis type="category" dataKey="ingredient" tick={{ fontSize: 9, fontFamily: "IBM Plex Sans" }} width={120} />
                <Tooltip />
                <Bar dataKey="count" fill="#F59E0B" name="Orders" />
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-sm text-zinc-500">No data yet</p>}
        </div>

        {/* Delivery Methods */}
        {deliveryData.length > 0 && (
          <div className="border border-zinc-200 p-6 lg:col-span-2" data-testid="delivery-methods-chart">
            <h3 className="font-[Chivo] font-bold text-zinc-950 mb-4">Delivery Methods</h3>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
              {deliveryData.map((d, i) => (
                <div key={i} className="text-center p-4 bg-zinc-50 border border-zinc-200">
                  <p className="font-mono text-xs uppercase tracking-wider text-zinc-500">{d.name}</p>
                  <p className="font-[Chivo] text-2xl font-black text-zinc-950 mt-1">{d.value}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Turnaround Time Section */}
      <div className="border border-zinc-200 mt-8" data-testid="turnaround-section">
        <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Timer size={18} className="text-[#0055FF]" />
            <h3 className="font-[Chivo] font-bold text-zinc-950">Estimated Turnaround Time</h3>
          </div>
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">
            Based on {ta.total_completed} delivered order{ta.total_completed === 1 ? "" : "s"}
          </p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-0">
          <div className="p-6 border-r border-b border-zinc-200" data-testid="turnaround-avg">
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">Avg Turnaround</p>
            <p className="font-[Chivo] text-3xl font-black text-zinc-950 mt-2">
              {ta.total_completed > 0 ? `${ta.avg_days}d` : "—"}
            </p>
            <p className="font-mono text-xs text-zinc-500 mt-1">
              {ta.total_completed > 0 ? `${ta.avg_hours}h total` : "No completed orders yet"}
            </p>
          </div>
          <div className="p-6 border-r border-b border-zinc-200" data-testid="turnaround-fastest">
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500 flex items-center gap-1">
              <Lightning size={12} weight="fill" className="text-emerald-600" /> Fastest
            </p>
            <p className="font-[Chivo] text-3xl font-black text-emerald-600 mt-2">
              {ta.total_completed > 0 ? formatDuration(ta.fastest_hours) : "—"}
            </p>
          </div>
          <div className="p-6 border-r border-b border-zinc-200" data-testid="turnaround-slowest">
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">Slowest</p>
            <p className="font-[Chivo] text-3xl font-black text-amber-600 mt-2">
              {ta.total_completed > 0 ? formatDuration(ta.slowest_hours) : "—"}
            </p>
          </div>
          <div className="p-6 border-b border-zinc-200" data-testid="turnaround-completed">
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">Completed</p>
            <p className="font-[Chivo] text-3xl font-black text-zinc-950 mt-2">{ta.total_completed}</p>
          </div>
        </div>

        {stageList.length > 0 && (
          <div className="p-6 border-b border-zinc-200" data-testid="turnaround-stages">
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500 mb-4">Average Time Per Stage</p>
            <div className="space-y-3">
              {stageList.map((s) => {
                const maxHours = Math.max(...stageList.map(x => x.avg_hours), 1);
                const pct = Math.max(2, (s.avg_hours / maxHours) * 100);
                return (
                  <div key={s.key} className="flex items-center gap-3">
                    <span className="font-mono text-xs text-zinc-700 w-48 truncate">{s.label}</span>
                    <div className="flex-1 h-6 bg-zinc-100 relative">
                      <div className="h-full bg-[#0055FF]" style={{ width: `${pct}%` }}></div>
                    </div>
                    <span className="font-mono text-xs font-semibold text-zinc-950 w-20 text-right">{formatDuration(s.avg_hours)}</span>
                    <span className="font-mono text-xs text-zinc-500 w-16 text-right">n={s.count}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {ta.by_month?.length > 0 && (
          <div className="p-6" data-testid="turnaround-trend-chart">
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500 mb-4">Monthly Turnaround Trend</p>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={ta.by_month}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                <XAxis dataKey="label" tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} />
                <YAxis tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} tickFormatter={v => `${v}d`} />
                <Tooltip formatter={(v, n) => [`${v} days`, "Avg Turnaround"]} />
                <Line type="monotone" dataKey="avg_days" stroke="#0055FF" strokeWidth={2} dot={{ fill: "#0055FF" }} name="Avg Days" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
}
